import styled, { css } from 'styled-components';
import getThemeByProps from '@userzoomlibraries/toolkit-theme-manager/get-theme-by-props';
import React, { useRef, useState, useMemo, useCallback, useEffect, useImperativeHandle } from 'react';
import isDefinedAndNotNull from '@userzoomlibraries/toolkit-js-utils/is-defined-and-not-null';
import isFunction from '@userzoomlibraries/toolkit-js-utils/is-function';
import useFocusable, { focusablePropTypes, focusableDefaultProps } from '@userzoomlibraries/toolkit-react-utils/use-focusable';
import useAriaAttributes from '@userzoomlibraries/toolkit-react-utils/use-aria-attributes';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import { jsx } from 'react/jsx-runtime';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var RESIZE_DIRECTIONS = Object.freeze({
  NONE: 'none',
  BOTH: 'both',
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical'
});

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty$1(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var PROP_TYPES = _objectSpread$1({
  id: PropTypes.stringWithDescription('Id of the textarea.'),
  // eslint-disable-next-line max-len
  autoFocus: PropTypes.boolWithDescription('Flag to focus the textarea on page load. The autoFocus prop should not be used, as [it can reduce usability and accessibility for users](https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/566011b89df811b95bc4e99799a5ea03d440411d/docs/rules/no-autofocus.md). '),
  autoGrow: PropTypes.boolWithDescription('Flag to enable/disable the height auto grow of the textarea.'),
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  defaultValue: PropTypes.stringWithDescription('Default value of the textarea.'),
  autoCapitalize: PropTypes.stringWithDescription('Flag to auto capitalize the text introduced. See [here](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/autocapitalize) for full list of values. **Only for mobile**'),
  autoComplete: PropTypes.stringWithDescription('Flag to specify what kind of assistance in filling the field. See [here](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#values) for full list of values.'),
  spellCheck: PropTypes.oneOfWithDescription(['true', 'false'], 'Flag to define if the textarea may be checked for spelling errors.'),
  form: PropTypes.stringWithDescription('Id of the form that the textarea belongs to.'),
  value: PropTypes.stringWithDescription('Value of the textarea (controlled).'),
  name: PropTypes.stringWithDescription('Name of the textarea.'),
  readOnly: PropTypes.boolWithDescription('Flag to make the textarea readonly.'),
  placeholder: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.func], 'Placeholder of the textarea. In case of function `() => String`'),
  maxLength: PropTypes.numberWithDescription('Maximum length of the textarea.'),
  minLength: PropTypes.numberWithDescription('Minimum length of the textarea.'),
  invalid: PropTypes.boolWithDescription('Flag to make the textarea invalid.'),
  cols: PropTypes.numberWithDescription('Number of columns that the textarea will have.'),
  rows: PropTypes.numberWithDescription('Number of rows that the textarea will have.'),
  resizeDirection: PropTypes.oneOfWithDescription(Object.values(RESIZE_DIRECTIONS), 'Direction of the textarea resize behaviour.'),
  onFocus: PropTypes.funcWithDescription('Callback called when focus event is triggered. `(event) => {}`'),
  onBlur: PropTypes.funcWithDescription('Callback called when blur event is triggered. `(event) => {}`'),
  onChange: PropTypes.funcWithDescription('Callback called when the value of the textarea has changed. `({ event, value }) => {}`')
}, focusablePropTypes);

var DEFAULT_PROPS = _objectSpread$1({
  id: undefined,
  autoFocus: false,
  autoGrow: false,
  className: '',
  autoCapitalize: undefined,
  autoComplete: 'on',
  spellCheck: undefined,
  defaultValue: undefined,
  form: undefined,
  name: null,
  readOnly: false,
  placeholder: null,
  maxLength: 524288,
  minLength: 0,
  invalid: false,
  cols: undefined,
  rows: undefined,
  resizeDirection: RESIZE_DIRECTIONS.BOTH,
  onFocus: noop,
  onBlur: noop,
  onChange: noop,
  onKeyDown: noop
}, focusableDefaultProps);

var _excluded = ["id", "className", "autoCapitalize", "autoComplete", "autoGrow", "spellCheck", "form", "name", "readOnly", "placeholder", "maxLength", "minLength", "cols", "rows", "defaultValue", "invalid", "value", "onFocus", "onBlur", "onChange"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var Textarea$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
      className = _ref.className,
      autoCapitalize = _ref.autoCapitalize,
      autoComplete = _ref.autoComplete,
      autoGrow = _ref.autoGrow,
      spellCheck = _ref.spellCheck,
      form = _ref.form,
      name = _ref.name,
      readOnly = _ref.readOnly,
      placeholder = _ref.placeholder,
      maxLength = _ref.maxLength,
      minLength = _ref.minLength,
      cols = _ref.cols,
      rows = _ref.rows,
      defaultValue = _ref.defaultValue,
      invalid = _ref.invalid,
      value = _ref.value,
      onFocus = _ref.onFocus,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var textareaRef = useRef();

  var _useState = useState(defaultValue || ''),
      _useState2 = _slicedToArray(_useState, 2),
      uncontrolledState = _useState2[0],
      setUncontrolledState = _useState2[1];

  var ariaAttributes = useAriaAttributes(otherProps, {
    excludedAttributes: ['aria-invalid']
  });
  var dataAttributes = useDataAttributes(otherProps);
  var textareaFocusableProps = useFocusable(textareaRef.current, otherProps);
  var isControlledTextarea = useMemo(function () {
    if (isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    if (!isDefinedAndNotNull(defaultValue) && isDefinedAndNotNull(value)) {
      return true;
    }

    if (!isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    console.error("Warning: ".concat(Textarea$1.displayName, " contains a textarea with both value and defaultValue props.\n      Textarea elements must be either controlled or uncontrolled (specify either the value prop, or the defaultValue prop, but not both).\n      Decide between using a controlled or uncontrolled textarea element and remove one of these props. More info: https://reactjs.org/link/controlled-components"));
    return false;
  }, [value, defaultValue]);
  var placeholderToRender = useMemo(function () {
    if (isFunction(placeholder)) {
      return placeholder();
    }

    return placeholder;
  }, [placeholder]);
  var onChangeHandler = useCallback(function (event) {
    var newValue = event.target.value;

    if (!isControlledTextarea) {
      setUncontrolledState(newValue);
    }

    onChange({
      event: event,
      value: newValue
    });
  }, [isControlledTextarea, onChange]);
  var onInputHandler = useCallback(function () {
    if (autoGrow) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = "".concat(textareaRef.current.scrollHeight, "px");
    }
  }, [autoGrow]);
  useEffect(function () {
    if (autoGrow && textareaRef.current) {
      textareaRef.current.style.height = "".concat(textareaRef.current.scrollHeight, "px");
    }
  }, [autoGrow]);
  useImperativeHandle(ref, function () {
    return textareaRef.current;
  });
  return /*#__PURE__*/jsx("textarea", _objectSpread(_objectSpread(_objectSpread({
    id: id,
    className: "gz-textarea ".concat(className),
    ref: textareaRef,
    value: isControlledTextarea ? value : uncontrolledState,
    onFocus: onFocus,
    onBlur: onBlur,
    onInput: onInputHandler,
    onChange: onChangeHandler,
    autoCapitalize: autoCapitalize,
    autoComplete: autoComplete,
    spellCheck: spellCheck,
    form: form,
    name: name,
    readOnly: readOnly,
    placeholder: placeholderToRender,
    maxLength: maxLength,
    minLength: minLength,
    cols: cols,
    rows: rows,
    "aria-invalid": invalid // eslint-disable-next-line react/jsx-props-no-spreading

  }, ariaAttributes), dataAttributes), textareaFocusableProps));
});
Textarea$1.COMPONENT_ID = 'Gearz.Textarea';
Textarea$1.displayName = Textarea$1.COMPONENT_ID;
Textarea$1.RESIZE_DIRECTIONS = RESIZE_DIRECTIONS;
Textarea$1.propTypes = PROP_TYPES;
Textarea$1.defaultProps = DEFAULT_PROPS;

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Textarea = styled(Textarea$1).withConfig({
  componentId: "gz-textarea-1673535308611__sc-yxn0r1-0"
})(["background-color:#fff;border:1px solid #474b6d;border-radius:4px;box-sizing:border-box;display:flex;line-height:20px;margin:0px;min-width:0;padding:6px;position:relative;width:100%;&:focus-within{outline:2px solid #314fff;outline-offset:2px;}&:not(:focus-within){&:not([aria-disabled=\"true\"]){&[aria-invalid=\"true\"]{border-color:#f00314;&:focus,&:focus-visible,&:hover{border-color:#474b6d;}}}}&[aria-disabled=\"true\"]{cursor:not-allowed;opacity:0.42;}", " ", " ", ""], function (_ref) {
  var _ref$resizeDirection = _ref.resizeDirection,
      resizeDirection = _ref$resizeDirection === void 0 ? RESIZE_DIRECTIONS.NONE : _ref$resizeDirection;
  return css(["resize:", ";"], resizeDirection);
}, function (_ref2) {
  var _ref2$autoGrow = _ref2.autoGrow,
      autoGrow = _ref2$autoGrow === void 0 ? false : _ref2$autoGrow;
  return autoGrow && css(["overflow:hidden;"]);
}, function (props) {
  return getThemeByProps({
    componentId: Textarea$1.COMPONENT_ID,
    props: props
  });
});
Textarea.displayName = Textarea$1.displayName;
Textarea.propTypes = Textarea$1.propTypes;
Textarea.defaultProps = Textarea$1.defaultProps;

export { Textarea as T };
